import {ActionIcon, Button, Checkbox, Paper, Select} from "@mantine/core";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import Page from "../../components/Page/Page";
import api, {useUser} from "../../utils/api";
import {DataTable} from "mantine-datatable";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Col, Row} from "../../components/Flex/Flex.tsx";
import {FloppyDiskIcon, TrashIcon} from "@phosphor-icons/react";
import MessageContext from "../../context/MessageContext.tsx";
import {User, FactionPermission, Faction, permissionKeys} from "../../types/general.types.ts";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import {bodyRelativeTimestamp} from "../subscription/shared.tsx";

const getFactionMembers = async (id: string | undefined) => {
    if (id === undefined) {
        return [];
    }
    const res = await api.get('/faction/manage/users/' + id)
    return res.data.sort((a: User, b: User) => a.handle.localeCompare(b.handle))
}

const getFactionPermissions = async (id: string | undefined): Promise<FactionPermission[]> => {
    if (id === undefined) {
        return [];
    }
    const res = await api.get('/faction/manage/permissions/' + id)
    return res.data;
}

const getNewPermissions = (user: User): FactionPermission => {
    return {
        user: user.uid,
        userName: user.handle,
        ...permissionKeys.reduce((acc, key) => ({...acc, [key]: false}), {})
    } as FactionPermission;
}

export default function FactionPrivs() {
    const {factions, user} = useUser();
    const [faction, setFaction] = useState<Faction | null>(null);
    const [localPermissions, setLocalPermissions] = useState<FactionPermission[]>([]);
    const {success} = useContext(MessageContext);
    const queryClient = useQueryClient();

    const {data: permissions, isLoading} = useQuery<FactionPermission[]>({
        queryKey: ['faction', 'permissions', faction?.id],
        queryFn: () => getFactionPermissions(faction?.id)
    })

    useEffect(() => {
        if (permissions) {
            setLocalPermissions(permissions);
        }
    }, [permissions]);

    const possibleFactions = useMemo(() => {
        const options: Faction[] = [];
        factions.forEach(f => {
            if ((f.permissions && f.permissions?.managePermissions) || f.leader?.id === user.uid || f.second?.id === user.uid) {
                options.push(f);
            }

        });
        if (options.length === 1) {
            setFaction(options[0]);
        }
        return options;
    }, [factions, user.uid]);

    const {data: members} = useQuery<User[]>({
        queryKey: ['faction', 'members', faction?.id],
        queryFn: () => getFactionMembers(faction?.id)
    })

    const memberOptions = useMemo(() => {
        if (!members) {
            return [];
        }
        return members.map(m => {
            return {...m, disabled: localPermissions?.some(p => p.user === m.uid)};
        })
    }, [members, localPermissions]);

    const factionSelectData = useMemo(() => {
        return possibleFactions.map(f => ({value: f.id, label: f.name}));
    }, [possibleFactions]);

    const memberSelectData = useMemo(() => {
        return memberOptions.map(m => ({value: m.uid, label: m.handle, disabled: m.disabled}));
    }, [memberOptions]);


    const updatePermission = useCallback((user: string, key: string, value: boolean) => {
        setLocalPermissions(current => current.map(permission =>
            permission.user === user
                ? {...permission, [key]: value}
                : permission
        ));
    }, []);

    const bodyPermission = useCallback((data: FactionPermission, options: {field: string}) => {
        return (
            <Checkbox
                checked={data[options.field as keyof Omit<FactionPermission, 'user' | 'userName'>]}
                onChange={(e) => {
                    updatePermission(data.user, options.field, e.currentTarget.checked);
                }}
            />
        );
    }, [updatePermission]);

    const bodyActions = useCallback((row: FactionPermission) => {
        return (
            <ActionIcon
                variant="subtle"
                onClick={() => {
                    setLocalPermissions(current => current.filter(p => p.user !== row.user));
                }}
            >
                <TrashIcon size={16} />
            </ActionIcon>
        );

    }, []);

    return (
        <Page>
            <title>Faction Privileges</title>
            <h1>Manage Faction Access</h1>
            {isLoading && <LoaderScreen message="Loading permissions" />}
            {possibleFactions.length > 1 &&
                <Select
                    value={faction?.id}
                    data={factionSelectData}
                    placeholder="Select a faction"
                    onChange={(val) => {
                        const f = factions.find(f => f.id === val);
                        if (f) {
                            setFaction(f)
                        }
                    }}
                />
            }
            <br />
            {faction &&
                <Paper className="mt-4">
                    <h3 className="mt-0">{faction.name}</h3>
                    <Row>
                        <Col>
                            <Select
                                data={memberSelectData}
                                placeholder="Add user"
                                value={null}
                                onChange={(val) => {
                                    const m = members?.find((m: User) => m.uid === val);
                                    if (m) {
                                        setLocalPermissions(current => [...current, getNewPermissions(m)]);
                                    }
                                }}
                            />
                        </Col>
                        <Col grow={1} />
                        <Col>
                            <Button leftSection={<FloppyDiskIcon size={16} />} onClick={() => {
                                api.post('/faction/manage/permissions', {faction: faction.id, permissions: localPermissions}).then(() => {
                                    success('Permissions saved');
                                    queryClient.invalidateQueries({queryKey: ['faction', 'permissions', faction?.id]});
                                })
                            }}>Save</Button>
                        </Col>
                    </Row>


                    <DataTable
                        records={localPermissions}
                        idAccessor="user"
                        columns={[
                            {accessor: 'userName', title: 'User'},
                            {accessor: 'lastActive', title: 'Last active', render: (row) => bodyRelativeTimestamp(members?.find(m => m.uid === row.user) ?? {}, {field: 'lastActive'})},
                            {accessor: 'manageTavernNetwork', title: 'Manage Taverns', render: (row) => bodyPermission(row, {field: 'manageTavernNetwork'})},
                            {accessor: 'manageLicenses', title: 'Manage Licenses', render: (row) => bodyPermission(row, {field: 'manageLicenses'})},
                            {accessor: 'managePermissions', title: 'Manage Permissions', render: (row) => bodyPermission(row, {field: 'managePermissions'})},
                            {accessor: 'actions', title: 'Actions', render: (row) => bodyActions(row)},
                        ]}
                    />

                </Paper>
            }
        </Page>
    )


}
